import { inject, Service } from '@angular/core'
import type { Observable } from 'rxjs'
import { catchError, map, of, ReplaySubject, take } from 'rxjs'
import localStorageHelper from '@awork/_shared/functions/local-storage'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import { getConfiguredPersistedStoreNames } from '@awork/core/state/signal-store/persistStateSchema'
import type { UserPermissions } from '@awork/features/workspace/models/permissions.model'
import { getProjectBudgetAccessKey } from '@awork/features/workspace/services/permission-service/permissions.helpers'

/** Stores that cache project budget data. Schedules and bookings carry no budget data. */
const PROJECT_BUDGET_DATA_STORE_NAMES = ['project', 'task', 'taskList', 'timeTracking']
const STORAGE_KEY_PREFIX = 'awProjectBudgetAccess_'

interface BudgetAccess {
  key: string
  projects: Record<string, boolean>
}

@Service()
export class ProjectBudgetAccessChangeService {
  private readonly signalStoreService = inject(SignalStoreService)

  private accessState: { userId: string; access: BudgetAccess | null; isResetting: boolean } | null = null
  private resetCompleted$: Observable<boolean> = of(true)

  /**
   * Resets cached budget data and reloads the app when budget access changes.
   * @param {UserPermissions} permissions - Permissions just loaded from the API.
   * @param {Record<string, boolean>} [projects={}] - Effective budget access for loaded projects.
   * @returns {Observable<boolean>} Emits after cleanup completes, or false if cleanup fails.
   */
  handlePermissionsLoaded(permissions: UserPermissions, projects: Record<string, boolean> = {}): Observable<boolean> {
    const accessKey = getProjectBudgetAccessKey(permissions)

    if (!accessKey) {
      return of(true)
    }

    const storageKey = STORAGE_KEY_PREFIX + permissions.userId
    const previousAccess =
      this.accessState?.userId === permissions.userId ? this.accessState.access : this.readStoredAccess(storageKey)

    if (this.accessState?.isResetting) {
      return this.resetCompleted$
    }

    const hasProjectAccessChanged = Object.entries(projects).some(
      ([projectId, canRead]) =>
        previousAccess?.projects[projectId] !== undefined && previousAccess.projects[projectId] !== canRead
    )
    const access: BudgetAccess = { key: accessKey, projects: { ...previousAccess?.projects, ...projects } }

    if (previousAccess?.key === accessKey && !hasProjectAccessChanged) {
      const hasNewProjects = Object.keys(projects).some(projectId => previousAccess.projects[projectId] === undefined)

      if (hasNewProjects) {
        this.recordAccess(permissions.userId, storageKey, access)
      }

      return of(true)
    }

    // Without a stored key and with enforcement off, no cached value can be stale.
    if (previousAccess === null && permissions.bpe !== true) {
      this.recordAccess(permissions.userId, storageKey, access)

      return of(true)
    }

    this.accessState = { userId: permissions.userId, access: previousAccess, isResetting: true }

    const resetCompleted = new ReplaySubject<boolean>(1)

    this.resetCompleted$ = resetCompleted.asObservable()

    this.resetBudgetStores().subscribe(isReset => {
      this.accessState.isResetting = false

      if (isReset) {
        this.recordAccess(permissions.userId, storageKey, access)

        // Initial loads fetch data after cleanup. Existing access changes still need a reload.
        // Skip reloads when storage is blocked to avoid reloading on every start.
        if (previousAccess !== null && this.readStoredAccess(storageKey)?.key === accessKey) {
          this.reloadApp()
        }
      }

      resetCompleted.next(isReset)
      resetCompleted.complete()
    })

    return this.resetCompleted$
  }

  /** Reloads the app to fetch data with the current permissions. */
  reloadApp(): void {
    window.location.reload()
  }

  /**
   * Resets budget data stores in memory and in persistence.
   * @returns {Observable<boolean>} Whether the cleanup finished. False only when deletion failed.
   */
  private resetBudgetStores(): Observable<boolean> {
    const exclude = [
      ...new Set([...this.signalStoreService.getRegisteredStores().keys(), ...getConfiguredPersistedStoreNames()])
    ].filter(name => !PROJECT_BUDGET_DATA_STORE_NAMES.includes(name))

    return this.signalStoreService.resetStoresAndWait({ exclude }).pipe(
      take(1),
      // False means no persisted rows needed deletion. The cleanup still finished. Only an error means it failed.
      map(() => true),
      catchError(() => of(false))
    )
  }

  /**
   * Reads the last completed budget access snapshot for this user.
   * @param {string} key - The local storage key.
   * @returns {BudgetAccess | null} The stored access, or null when absent.
   */
  private readStoredAccess(key: string): BudgetAccess | null {
    try {
      const raw = localStorageHelper.getItem(key)

      if (!raw) {
        return null
      }

      const stored = JSON.parse(raw) as { key?: string; projects?: Record<string, boolean> }

      return stored.key ? { key: stored.key, projects: stored.projects ?? {} } : null
    } catch {
      return null
    }
  }

  /**
   * Records access only after cleanup succeeds or no cleanup is needed.
   * @param {string} userId - User whose access is recorded.
   * @param {string} key - The local storage key.
   * @param {BudgetAccess} access - The current budget access snapshot.
   */
  private recordAccess(userId: string, key: string, access: BudgetAccess): void {
    this.accessState = { userId, access, isResetting: false }
    localStorageHelper.setItem(key, access)
  }
}
